/**
 * Auth store — full implementation (TASK-13).
 *
 * State (`user` / `accessToken` / `refreshToken`) is hydrated from
 * `services/tokens` (localStorage) at store creation so router guards keep
 * working after a page reload. All actions hit the backend through the shared
 * `api` axios instance and keep both the reactive state and the persisted
 * storage in sync.
 *
 * Error handling: `login`/`register` never throw — they capture the failure
 * into `loginError` / `registerError` (plus a machine-readable `*ErrorCode`)
 * and return a boolean success flag, so views can render inline alerts without
 * try/catch. `refresh`/`fetchMe` return booleans; `logout` is best-effort and
 * always clears locally.
 *
 * Backend contract (TASK-04, see ARCHITECTURE.md §8):
 *   POST /auth/register { email, username, password } -> 201 { user }
 *   POST /auth/login    { email, password }           -> 200 { accessToken, refreshToken, user }
 *   POST /auth/refresh  { refreshToken }              -> 200 { accessToken, refreshToken }
 *   POST /auth/logout   { refreshToken }              -> 204
 *   GET  /users/me                                  -> 200 { user }
 * Uniform error body: `{ error: { message, code, details? } }`.
 */
import axios, { type AxiosRequestConfig } from 'axios'
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import type {
  ChangePasswordPayload,
  LoginPayload,
  LoginResponse,
  RefreshResponse,
  RegisterPayload,
  Role,
  UpdateMePayload,
  User,
} from '@/types'
import { api } from '@/services/api'
import {
  clearTokens,
  getAccessToken,
  getRefreshToken,
  getUser,
  setTokens,
  setUser as persistUser,
} from '@/services/tokens'

/** Shape of the backend's uniform error body. */
interface ApiErrorBody {
  error: { message: string; code: string; details?: unknown }
}

/** Normalised error: a human message + the backend code (if any). */
interface NormalisedError {
  message: string
  code: string | null
}

/**
 * Per-request flag understood by `services/api.ts`: when present, the 401
 * refresh-retry interceptor treats the request as untouchable and rejects with
 * the *original* error. Applied to auth-flow endpoints so that, for example, an
 * "invalid credentials" 401 on `/auth/login` surfaces to the caller instead of
 * being swallowed by the single-flight refresh machinery (which would otherwise
 * turn it into a generic "No refresh token available" on the login page).
 *
 * `mergeConfig` in axios preserves unknown keys, so this reaches the response
 * interceptor's `error.config` intact.
 */
type AuthRequestConfig = AxiosRequestConfig & { _isRefreshRequest?: boolean }
const SKIP_REFRESH: AuthRequestConfig = { _isRefreshRequest: true }

/**
 * Convert a thrown value into a localised Russian message + backend code.
 * Known codes are translated; unknown codes fall back to the server message.
 */
function normaliseError(err: unknown): NormalisedError {
  if (axios.isAxiosError(err)) {
    const body = err.response?.data as ApiErrorBody | undefined
    const code = body?.error?.code ?? null
    const serverMessage = body?.error?.message

    switch (code) {
      case 'ACCOUNT_NOT_APPROVED':
        return { message: 'Аккаунт ожидает подтверждения администратором.', code }
      case 'INVALID_CREDENTIALS':
        return { message: 'Неверный email или пароль.', code }
      case 'EMAIL_TAKEN':
        return { message: 'Этот email уже зарегистрирован.', code }
      case 'USERNAME_TAKEN':
        return { message: 'Это имя пользователя уже занято.', code }
      case 'EMAIL_TAKEN':
        return { message: 'Этот email уже зарегистрирован.', code }
      case 'INVALID_CURRENT_PASSWORD':
        return { message: 'Неверный текущий пароль.', code }
      case 'PASSWORD_SAME':
        return { message: 'Новый пароль должен отличаться от текущего.', code }
      case 'VALIDATION_ERROR':
        return { message: serverMessage ?? 'Проверьте введённые данные.', code }
      case 'INVALID_REFRESH_TOKEN':
        return { message: 'Сессия истекла. Войдите снова.', code }
    }

    if (serverMessage) return { message: serverMessage, code }
    if (err.response) return { message: `Ошибка сервера (${err.response.status}).`, code }
    if (err.request) {
      return { message: 'Сервер недоступен. Проверьте подключение к сети.', code }
    }
  }
  return {
    message: err instanceof Error ? err.message : 'Произошла непредвиденная ошибка.',
    code: null,
  }
}

export const useAuthStore = defineStore('auth', () => {
  // ── State (hydrated from localStorage) ─────────────────────────────────────
  const user = ref<User | null>(getUser())
  const accessToken = ref<string | null>(getAccessToken())
  const refreshToken = ref<string | null>(getRefreshToken())

  // UI-facing error state for the auth screens.
  const loginError = ref<string | null>(null)
  const loginErrorCode = ref<string | null>(null)
  const registerError = ref<string | null>(null)
  const registerErrorCode = ref<string | null>(null)

  // ── Getters ────────────────────────────────────────────────────────────────
  const isAuthenticated = computed(() => !!accessToken.value)
  const role = computed<Role | null>(() => user.value?.role ?? null)
  const isAdmin = computed(() => role.value === 'ADMIN')

  // ── Internal helpers ───────────────────────────────────────────────────────

  /** Re-read state from localStorage (e.g. after an external refresh). */
  function hydrate(): void {
    user.value = getUser()
    accessToken.value = getAccessToken()
    refreshToken.value = getRefreshToken()
  }

  /** Persist a fresh token pair + user and mirror them into reactive state. */
  function setSession(
    tokens: { accessToken: string; refreshToken: string },
    nextUser: User,
  ): void {
    setTokens(tokens.accessToken, tokens.refreshToken)
    persistUser(nextUser)
    accessToken.value = tokens.accessToken
    refreshToken.value = tokens.refreshToken
    user.value = nextUser
  }

  /** Wipe persisted auth data and reactive state (incl. error fields). */
  function clear(): void {
    clearTokens()
    user.value = null
    accessToken.value = null
    refreshToken.value = null
    loginError.value = null
    loginErrorCode.value = null
    registerError.value = null
    registerErrorCode.value = null
  }

  // ── Actions ────────────────────────────────────────────────────────────────

  /**
   * Authenticate and store the session. Never throws — on failure the error is
   * captured into `loginError`/`loginErrorCode` and `false` is returned.
   * A 403 (`ACCOUNT_NOT_APPROVED`) does not create a session; the view uses the
   * code to offer a link to `/pending`.
   */
  async function login(email: string, password: string): Promise<boolean> {
    loginError.value = null
    loginErrorCode.value = null
    try {
      const payload: LoginPayload = { email, password }
      const { data } = await api.post<LoginResponse>(
        '/auth/login',
        payload,
        SKIP_REFRESH,
      )
      setSession(
        { accessToken: data.accessToken, refreshToken: data.refreshToken },
        data.user,
      )
      return true
    } catch (err) {
      const { message, code } = normaliseError(err)
      loginError.value = message
      loginErrorCode.value = code
      return false
    }
  }

  /**
   * Register a new PENDING account. Does NOT log in (the account must be
   * approved by an admin first — see ARCHITECTURE.md §8). Never throws —
   * failures land in `registerError` and `false` is returned; on success
   * `true` is returned (the view then redirects to `/pending`).
   */
  async function register(
    email: string,
    username: string,
    password: string,
  ): Promise<boolean> {
    registerError.value = null
    registerErrorCode.value = null
    try {
      const payload: RegisterPayload = { email, username, password }
      await api.post('/auth/register', payload, SKIP_REFRESH)
      return true
    } catch (err) {
      const { message, code } = normaliseError(err)
      registerError.value = message
      registerErrorCode.value = code
      return false
    }
  }

  /**
   * Explicitly rotate the token pair. Note: `services/api.ts` already performs
   * an inline single-flight refresh on 401; this action covers explicit flows
   * (e.g. a pre-emptive refresh). On failure the session is cleared. Returns
   * `true` on success.
   */
  async function refresh(): Promise<boolean> {
    const currentRefresh = getRefreshToken()
    if (!currentRefresh) return false
    try {
      const { data } = await api.post<RefreshResponse>(
        '/auth/refresh',
        { refreshToken: currentRefresh },
        SKIP_REFRESH,
      )
      // Only the tokens rotate; the cached user stays the same.
      setTokens(data.accessToken, data.refreshToken)
      accessToken.value = data.accessToken
      refreshToken.value = data.refreshToken
      return true
    } catch {
      clear()
      return false
    }
  }

  /**
   * Revoke the current refresh token server-side (best-effort — errors are
   * ignored so logout always succeeds locally) and clear all auth state.
   */
  async function logout(): Promise<void> {
    const currentRefresh = getRefreshToken()
    if (currentRefresh) {
      try {
        await api.post('/auth/logout', { refreshToken: currentRefresh }, SKIP_REFRESH)
      } catch {
        // Ignore: we clear locally regardless of the server response.
      }
    }
    clear()
  }

  /**
   * Fetch the current profile (`GET /users/me`) and refresh the cached user in
   * state + localStorage. A 401 here is allowed to flow through the `api`
   * interceptor (which performs an inline refresh + retry). Returns `true` on
   * success.
   */
  async function fetchMe(): Promise<boolean> {
    try {
      const { data } = await api.get<{ user: User }>('/users/me')
      persistUser(data.user)
      user.value = data.user
      return true
    } catch {
      return false
    }
  }

  /**
   * Update the current user's editable profile fields (`username` and/or
   * `email`). On success the cached `user` is refreshed (in state + localStorage)
   * and a localised error message is returned; never throws.
   *
   * @returns `{ ok: boolean, error: string | null }`.
   */
  async function updateProfile(
    input: UpdateMePayload,
  ): Promise<{ ok: boolean; error: string | null }> {
    try {
      const { data } = await api.patch<{ user: User }>('/users/me', input)
      persistUser(data.user)
      user.value = data.user
      return { ok: true, error: null }
    } catch (err: unknown) {
      return { ok: false, error: normaliseError(err).message }
    }
  }

  /**
   * Change the current user's password. The backend verifies the current
   * password; on success returns `ok: true`. Never throws — failures are
   * surfaced as a localised `error` string.
   */
  async function changePassword(
    input: ChangePasswordPayload,
  ): Promise<{ ok: boolean; error: string | null }> {
    try {
      await api.post('/users/me/password', input)
      return { ok: true, error: null }
    } catch (err: unknown) {
      return { ok: false, error: normaliseError(err).message }
    }
  }

  return {
    // state
    user,
    accessToken,
    refreshToken,
    loginError,
    loginErrorCode,
    registerError,
    registerErrorCode,
    // getters
    isAuthenticated,
    role,
    isAdmin,
    // actions
    login,
    register,
    refresh,
    logout,
    fetchMe,
    updateProfile,
    changePassword,
    hydrate,
    setSession,
    clear,
  }
})
